import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "@shadcn/lib/utils"

/**
 * UnderlinedTabs - Navigation-style tabs with underline for active state
 * Uses Radix UI Tabs primitives with scrollable, underlined tab list
 */

export function UnderlinedTabs({ children, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
	return <TabsPrimitive.Root {...props}>{children}</TabsPrimitive.Root>
}

export function UnderlinedTabsList({ children, className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
	return (
		<div className="relative w-full mb-6">
			{/* Scrollable container */}
			<div className="overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
				<TabsPrimitive.List
					className={cn(
						// Clean base styles - no overrides needed!
						"inline-flex",
						"w-max",
						"min-w-full",
						"justify-start",
						"items-center",
						"gap-0 relative",
						className
					)}
					{...props}>
					{children}
				</TabsPrimitive.List>
			</div>
			{/* Full-width bottom border */}
			<div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border" />
		</div>
	)
}

export function UnderlinedTabsTrigger({
	children,
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
	return (
		<TabsPrimitive.Trigger
			className={cn(
				// Clean base styles - no !important needed!
				"inline-flex items-center justify-center",
				"whitespace-nowrap",
				"bg-transparent",
				"border-none",
				"shadow-none",
				// Responsive spacing and typography
				"px-4 py-3 lg:px-8 lg:py-3.5",
				"text-sm sm:text-base font-medium",
				"min-w-[100px] sm:min-w-[140px]",
				// Default state (inactive)
				"text-muted-foreground",
				// Hover state
				"hover:text-foreground",
				// Active state
				"data-[state=active]:text-foreground",
				// Focus styles
				"focus-visible:outline-none",
				"focus-visible:ring-2",
				"focus-visible:ring-ring",
				"focus-visible:ring-offset-2",
				// Cursor and transitions
				"cursor-pointer",
				"transition-colors",
				// Disabled state
				"disabled:pointer-events-none",
				"disabled:opacity-50",
				// Position for pseudo-element
				"relative",
				// Pseudo-element for underline
				"after:content-['']",
				"after:absolute",
				"after:bottom-0",
				"after:left-0",
				"after:right-0",
				"after:h-[2px]",
				"after:bg-transparent",
				"after:z-10",
				// Active underline with primary color
				"data-[state=active]:after:bg-primary",
				className
			)}
			{...props}>
			{children}
		</TabsPrimitive.Trigger>
	)
}

export const UnderlinedTabsContent = TabsPrimitive.Content
